import { useCallback, useMemo } from 'react';
import * as globalState from '@/state';
import { Number3 } from '@abyss/3d-viewer';
import { TextMarker } from '../../shared/Viewer/TextMarker';
import { Zone } from '@/__generated__/graphql';
import { useAtom, useAtomValue } from 'jotai';

// const pointAsNumber3 = (point: Point3d): Number3 => {
//   return [point.x, point.y, point.z];
// };

// const add = (a: Number3, b: Number3): Number3 =>
//   a.map((value, index) => value + b[index]) as Number3;
const subtract = (a: Number3, b: Number3): Number3 =>
  a.map((value, index) => value - b[index]) as Number3;

const zoneCenterPoint = (zone: Zone): Number3 => {
  const { min, max } = zone.box;
  return [(min.x + max.x) * 0.5, (min.y + max.y) * 0.5, (min.z + max.z) * 0.5];
};

const calculateDistance = (a: Number3, b: Number3): number => {
  const diff = subtract(a, b);
  return diff[0] * diff[0] + diff[1] * diff[1] + diff[2] * diff[2];
};

export const NeighbouringZones = () => {
  const [selectedZone, setSelectedZone] = useAtom(globalState.selectedZone);
  const zones = useAtomValue(globalState.zones);

  const findZonesByDirection = useCallback(
    (currentZone: Zone) => {
      if (!zones) return undefined;

      const distanceByDirection: (undefined | number)[][] = [
        [undefined, undefined], //  x: -1, +1
        [undefined, undefined], //  y: -1, +1
        [undefined, undefined], //  z: -1, +1
      ];

      const zoneByDirection: (undefined | Zone)[][] = [
        [undefined, undefined], //  x: -1, +1
        [undefined, undefined], //  y: -1, +1
        [undefined, undefined], //  z: -1, +1
      ];

      const currentCenter = zoneCenterPoint(currentZone);

      zones.forEach((zone) => {
        if (zone === currentZone) return;
        const center = zoneCenterPoint(zone);
        const diff = subtract(center, currentCenter);
        const absDiff = diff.map((value) => Math.abs(value));
        const dominantIndex = absDiff.reduce((accumulator, value, index) => {
          return value > absDiff[accumulator] ? index : accumulator;
        }, 0);

        const distance = calculateDistance(center, currentCenter);

        const signIndex = diff[dominantIndex] > 0 ? 1 : 0;
        const bestDistance = distanceByDirection[dominantIndex][signIndex];
        if (!bestDistance || distance < bestDistance) {
          distanceByDirection[dominantIndex][signIndex] = distance;
          zoneByDirection[dominantIndex][signIndex] = zone;
        }
      });
      // console.log({ zoneByDirection });
      return zoneByDirection;
    },
    [zones]
  );

  const neighbouringZones = useMemo(
    () => selectedZone && findZonesByDirection(selectedZone),
    [selectedZone, findZonesByDirection]
  );

  return (
    <>
      {neighbouringZones?.flat().map((zone: Zone | undefined) => {
        return (
          zone && (
            <TextMarker
              text={zone.name}
              key={zone.name}
              position={zoneCenterPoint(zone)}
              onClick={() => {
                setSelectedZone(zone);
              }}
            />
          )
        );
      })}
    </>
  );
};
